import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import {
  ChangeDraftVm,
  ChangePackageStatus,
} from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import { ExitEditingChoice } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { ChangePackageStep } from '@/features/dashboard/bookingChanges/routes/changePackageSearch.ts';
import useCancelDraft from '@/features/dashboard/bookingChanges/hooks/useCancelDraft.ts';
import useCreateOrGetDraft from '@/features/dashboard/bookingChanges/hooks/useCreateOrGetDraft.ts';
import {
  ChangeEditAvailabilityVm,
  resolveEditAvailability,
} from './editAvailability';

interface UseExitEditingParams {
  bookingId: string;
  /** Otwarty szkic; `null` — nie ma czego porzucać, wyjście jest natychmiastowe. */
  draft: ChangeDraftVm | null;
  /** Stan paczki — paczka wysłana klientowi blokuje tryb edycji. */
  draftStatus?: ChangePackageStatus | null;
  /** Przejście ekranu w `?mode=edit`. */
  onEnterEditing: () => void;
  /** Powrót ekranu do `?mode=view`. */
  onExitToView: () => void;
}

interface UseExitEditingResult {
  availability: ChangeEditAvailabilityVm;
  isExitModalOpen: boolean;
  isDiscarding: boolean;
  /** Przełącznik „Tryb edycji”: włączenie wchodzi w edycję, wyłączenie pyta. */
  toggleEditing: (nextEditing: boolean) => void;
  requestExit: () => void;
  dismissExit: () => void;
  /**
   * Zdejmuje paczkę czekającą na decyzję klienta — po potwierdzeniu. Ta sama
   * operacja co odrzucenie szkicu: backend zna jedno „anuluj" dla paczki w
   * stanie nieostatecznym.
   */
  withdrawPackage: () => void;
  resolveExit: (choice: ExitEditingChoice) => void;
}

/**
 * Zachowanie wejścia i wyjścia z trybu edycji — wspólne dla ekranu uczestnika,
 * zapisującego i rezerwacji.
 *
 * Wejście (`VIEW → EDIT`) jest natychmiastowe i po drodze zakłada szkic, jeśli
 * jeszcze nie istnieje. Wyjście prowadzi **wyłącznie** przez modal z trzema
 * jawnymi wynikami; przy nieudanym odrzuceniu szkicu ekran zostaje w edycji,
 * żeby operator nie stracił pozycji na cichym błędzie sieci.
 */
const useExitEditing = ({
  bookingId,
  draft,
  draftStatus,
  onEnterEditing,
  onExitToView,
}: UseExitEditingParams): UseExitEditingResult => {
  const navigate = useNavigate();
  const { confirmDeleteWithCallback } = confirmDialogUseHook();
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const createOrGetDraft = useCreateOrGetDraft(bookingId);
  const cancelDraft = useCancelDraft(bookingId);

  const availability = resolveEditAvailability(draftStatus);

  const dismissExit = () => setIsExitModalOpen(false);

  const requestExit = () => {
    if (!draft) {
      onExitToView();
      return;
    }
    setIsExitModalOpen(true);
  };

  // Ekran przechodzi w tryb edycji dopiero, gdy szkic naprawdę istnieje —
  // inaczej nieudane założenie paczki zostawiałoby operatora w edycji bez
  // czego edytować (komunikat pokazuje sam `useCreateOrGetDraft`).
  const enterEditing = () => {
    if (!availability.canEdit) {
      return;
    }
    createOrGetDraft.mutate(undefined, { onSuccess: () => onEnterEditing() });
  };

  const toggleEditing = (nextEditing: boolean) =>
    nextEditing ? enterEditing() : requestExit();

  const resolveExit = (choice: ExitEditingChoice) => {
    if (!draft) {
      dismissExit();
      onExitToView();
      return;
    }

    switch (choice) {
      case ExitEditingChoice.APPLY:
        dismissExit();
        navigate({
          to: '/app/bookings/$bookingId/changes/$changeId',
          params: { bookingId, changeId: draft.id },
          search: { step: ChangePackageStep.SUMMARY },
        });
        return;

      case ExitEditingChoice.SAVE_DRAFT:
        dismissExit();
        toast.success('Szkic zapisany', {
          description: 'Wrócisz do niego z belki nad zakładkami.',
        });
        onExitToView();
        return;

      case ExitEditingChoice.DISCARD:
        // Ekran zostaje w edycji, dopóki backend nie potwierdzi odrzucenia —
        // komunikat o błędzie pokazuje sam `useCancelDraft`.
        cancelDraft.mutate(draft.id, {
          onSuccess: () => {
            dismissExit();
            toast.success('Szkic zmian został odrzucony');
            onExitToView();
          },
        });
        return;
    }
  };

  /*
   * Zdjęcie paczki kasuje pracę operatora i unieważnia link wysłany klientowi,
   * więc idzie przez ten sam globalny dialog co odrzucenie szkicu.
   */
  const withdrawPackage = () => {
    if (!draft) {
      return;
    }
    confirmDeleteWithCallback({
      title: 'Zdjąć paczkę od zapisującego?',
      description:
        'Paczka przestanie istnieć, a link wysłany zapisującemu przestanie działać. Rezerwacja wróci do edycji, ale zmian z paczki nie da się przywrócić.',
      confirmText: 'Zdejmij paczkę',
      loadingText: 'Zdejmowanie…',
      onConfirm: async () => {
        await cancelDraft.mutateAsync(draft.id);
        toast.success('Paczka zdjęta', {
          description:
            'Link dla zapisującego przestał działać, edycja jest znów otwarta.',
        });
      },
    });
  };

  return {
    availability,
    isExitModalOpen,
    isDiscarding: cancelDraft.isPending,
    withdrawPackage,
    toggleEditing,
    requestExit,
    dismissExit,
    resolveExit,
  };
};

export default useExitEditing;
